/*
 * 52 ARCHIVES mobile Quick Add (below 750px).
 * One shared bottom sheet for the collection-page and department-carousel cards ([data-quick-add-sheet-url]).
 * Its content is sections/quick-add-sheet.liquid, fetched per product through the Section Rendering API,
 * so sizes, prices and availability are Shopify's. Adding uses Dawn's <product-form> (/cart/add.js);
 * on mobile the add goes to the existing cart drawer instead of the cart notification, as on the
 * product page (product-lvr.js). Single-variant cards keep Dawn's inline form and are routed the same way.
 * Desktop keeps Dawn's behaviour: the cards' quick add is hidden there and the sheet never opens.
 */
(() => {
  if (window.quickAddSheetReady) return;
  window.quickAddSheetReady = true;

  const mobileQuery = window.matchMedia('(max-width: 749px)');
  const SECTION_ID = 'quick-add-sheet';
  const OPENER_SELECTOR = '[data-quick-add-sheet-url]';
  const CARD_FORM_SELECTOR = '.collection-lvr .card product-form';
  const CACHE_TTL = 10000;
  const CLOSE_FALLBACK_MS = 400;

  /* ---------- Section responses (prefetched on touch, reused on tap) ---------- */

  const responses = new Map();

  function sectionUrl(productUrl) {
    const url = new URL(productUrl, window.location.origin);
    url.searchParams.set('section_id', SECTION_ID);
    return url.toString();
  }

  function loadSheet(productUrl) {
    const key = sectionUrl(productUrl);
    const cached = responses.get(key);
    if (cached && Date.now() - cached.time < CACHE_TTL) return cached.promise;

    const promise = fetch(key).then((response) => {
      if (!response.ok) throw new Error(`Quick add: ${response.status}`);
      return response.text();
    });
    promise.catch(() => responses.delete(key));
    responses.set(key, { time: Date.now(), promise });
    return promise;
  }

  /* ---------- Cart target: the drawer on mobile ---------- */

  function routeToCartDrawer(productForm) {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!('quickAddDefaultCart' in productForm)) productForm.quickAddDefaultCart = productForm.cart;
    productForm.cart = mobileQuery.matches && cartDrawer ? cartDrawer : productForm.quickAddDefaultCart;
  }

  function setOpenerLoading(opener, loading) {
    opener.classList.toggle('loading', loading);
    opener.toggleAttribute('aria-disabled', loading);
    opener.querySelector('.loading__spinner')?.classList.toggle('hidden', !loading);
  }

  /* ---------- Sheet ---------- */

  class QuickAddSheet extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;

      this.innerHTML =
        '<div class="quick-add-sheet__overlay" data-quick-add-sheet-close></div>' +
        '<div class="quick-add-sheet__panel" role="dialog" aria-modal="true" tabindex="-1">' +
        '<div class="quick-add-sheet__content"></div></div>';
      this.panel = this.querySelector('.quick-add-sheet__panel');
      this.content = this.querySelector('.quick-add-sheet__content');

      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-quick-add-sheet-close]')) this.close();
      });
      this.addEventListener('keyup', (event) => {
        if (event.code === 'Escape') this.close();
      });
      this.addEventListener('change', (event) => {
        const input = event.target.closest('.quick-add-sheet__size-input');
        if (!input) return;
        this.variantInput.value = input.value;
        this.setAlert(false);
        this.productForm?.handleErrorMessage();
      });
    }

    get isOpen() {
      return this.hasAttribute('open');
    }

    get variantInput() {
      return this.content.querySelector('[data-quick-add-sheet-variant]');
    }

    get productForm() {
      return this.content.querySelector('product-form');
    }

    setAlert(visible) {
      const alert = this.content.querySelector('[data-quick-add-sheet-alert]');
      if (!alert) return;
      if (visible && !alert.hidden) {
        // Already shown: re-insert the text so screen readers announce it again.
        const text = alert.textContent;
        alert.textContent = '';
        requestAnimationFrame(() => (alert.textContent = text));
      }
      alert.hidden = !visible;
    }

    async open(opener) {
      if (this.pendingOpener) return;
      this.pendingOpener = opener;
      setOpenerLoading(opener, true);

      try {
        const html = await loadSheet(opener.dataset.quickAddSheetUrl);
        if (!mobileQuery.matches) return;
        const source = new DOMParser()
          .parseFromString(html, 'text/html')
          .querySelector('[data-quick-add-sheet-content]');
        if (!source) throw new Error('Quick add: empty response');

        this.finishClose();
        this.content.replaceChildren(document.importNode(source, true));
        this.panel.setAttribute('aria-labelledby', 'QuickAddSheet-Title');
        this.opener = opener;
        this.show();
      } catch (error) {
        console.error(error);
        // Fall back to the product page rather than leaving the tap without a result.
        window.location.href = opener.dataset.quickAddSheetUrl;
      } finally {
        setOpenerLoading(opener, false);
        this.pendingOpener = null;
      }
    }

    show() {
      clearTimeout(this.closeTimer);
      document.body.classList.add('overflow-hidden');
      this.setAttribute('open', '');
      // Reading the layout commits the closed position first, so the panel slides in.
      this.panel.getBoundingClientRect();
      this.classList.add('is-active');
      trapFocus(this.panel, this.panel);
    }

    // restoreFocus: back to the "+" (normal close). keepScrollLock: the cart drawer is about to open.
    close({ restoreFocus = true, keepScrollLock = false } = {}) {
      if (!this.isOpen || this.closing) return;
      this.closing = true;
      this.classList.remove('is-active');
      removeTrapFocus(restoreFocus ? this.opener : null);
      if (!keepScrollLock) document.body.classList.remove('overflow-hidden');

      const done = () => this.finishClose();
      this.panel.addEventListener('transitionend', done, { once: true });
      this.closeTimer = setTimeout(done, CLOSE_FALLBACK_MS);
    }

    finishClose() {
      clearTimeout(this.closeTimer);
      this.closing = false;
      if (this.classList.contains('is-active')) return;
      this.removeAttribute('open');
      this.content.replaceChildren();
    }
  }

  customElements.define('quick-add-sheet', QuickAddSheet);

  function getSheet() {
    let sheet = document.querySelector('quick-add-sheet');
    if (!sheet) {
      sheet = document.createElement('quick-add-sheet');
      document.body.appendChild(sheet);
    }
    return sheet;
  }

  /* ---------- Openers ---------- */

  document.addEventListener(
    'pointerdown',
    (event) => {
      const opener = event.target.closest(OPENER_SELECTOR);
      if (opener && mobileQuery.matches) loadSheet(opener.dataset.quickAddSheetUrl).catch(() => {});
    },
    { passive: true }
  );

  document.addEventListener('click', (event) => {
    const opener = event.target.closest(OPENER_SELECTOR);
    if (!opener) return;
    event.preventDefault();
    if (opener.getAttribute('aria-disabled') === 'true') return;
    if (!mobileQuery.matches) {
      window.location.href = opener.dataset.quickAddSheetUrl;
      return;
    }
    getSheet().open(opener);
  });

  /* ---------- Add to bag ---------- */

  // Capture phase on the document runs before Dawn's submit handler on the form.
  document.addEventListener(
    'submit',
    (event) => {
      const productForm = event.target.closest('product-form');
      if (!productForm) return;
      const sheet = productForm.closest('quick-add-sheet');
      if (!sheet && !productForm.matches(CARD_FORM_SELECTOR)) return;

      if (sheet && !sheet.variantInput?.value) {
        event.preventDefault();
        event.stopImmediatePropagation();
        sheet.setAlert(true);
        return;
      }
      routeToCartDrawer(productForm);
    },
    true
  );

  // A successful add publishes cartUpdate before product-form asks the drawer to render and open,
  // so the sheet closes first; the scroll lock stays on for the drawer.
  subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
    responses.clear();
    const sheet = document.querySelector('quick-add-sheet');
    if (!sheet?.isOpen || event?.source !== 'product-form') return;
    if (String(event.productVariantId) !== sheet.variantInput?.value) return;

    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && sheet.opener) cartDrawer.setActiveElement(sheet.opener);
    sheet.close({ restoreFocus: !cartDrawer, keepScrollLock: Boolean(cartDrawer) });
  });

  /* ---------- Leaving mobile / back-forward cache ---------- */

  mobileQuery.addEventListener('change', () => {
    if (!mobileQuery.matches) document.querySelector('quick-add-sheet')?.close();
  });

  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    const sheet = document.querySelector('quick-add-sheet');
    if (sheet?.isOpen) {
      sheet.close({ restoreFocus: false });
      sheet.finishClose();
    }
  });
})();
